import { buildUrl } from "@/lib/url";

export type JsonLDFounder = {
  name: string;
};

export type JsonLDBreadcrumb = {
  name: string;
  item: string;
};

export type JsonLDPublisher = {
  _id?: string;
};

export type JsonLDBreadcrumbReference = {
  _id: string;
};

export type JsonLDIsPartOf = {
  name: string;
  url: string;
};

export type JsonLDFAQPageEntity = {
  question: string;
  answer: string;
};

export type JsonLDServiceMainEntity = {
  name: string;
  description?: string;
  serviceType?: string;
  provider?: {
    name: string;
    url: string;
  };
  potentialAction?: {
    name: string;
    target: string;
  };
  category?: string;
  areaServed?: {
    "@type": string;
    name: string;
  }[];
};

export type JsonLDCollectionPageEntity = {
  itemListUrls: string[];
};

export type JsonLDPersonMainEntity = {
  name: string;
  jobTitle?: string;
  image?: string;
  url?: string;
  description?: string;
  knowsAbout?: string[];
  sameAs?: string[];
  worksFor?: {
    "@type": string;
    name: string;
  };
};

export type JsonLDMainEntity =
  | {
      "@type": "FAQPage";
      mainEntity: JsonLDFAQPageEntity[];
    }
  | {
      "@type": "Service";
      mainEntity: JsonLDServiceMainEntity;
    }
  | {
      "@type": "CollectionPage";
      mainEntity: JsonLDCollectionPageEntity;
    }
  | {
      "@type": "Person";
      mainEntity: JsonLDPersonMainEntity;
    };

export type JsonLDPostalAddress = {
  "@type": "PostalAddress";
  streetAddress?: string;
  addressLocality?: string;
  addressRegion?: string;
  postalCode?: string;
  addressCountry?: string;
};

export type JsonLDGeo = {
  "@type": "GeoCoordinates";
  latitude: number;
  longitude: number;
};

export type JsonLDOffer = {
  "@type": "Offer";
  price: number;
  priceCurrency: string;
  businessFunction?: string;
  availability?: string;
  url?: string;
  priceValidUntil?: string;
};

export type JsonLDProps = {
  id?: string;
  type:
    | "WebSite"
    | "WebPage"
    | "Blog"
    | "Article"
    | "NewsArticle"
    | "BlogPosting"
    | "Product"
    | "Organization"
    | "ProfilePage"
    | "RealEstateAgent"
    | "FAQPage"
    | "SearchResultsPage"
    | "BreadcrumbList"
    | "LocalBusiness"
    | "CollectionPage"
    | "WebApplication"
    | "SoftwareApplication"
    | "DefinedTerm";
  name?: string;
  path?: string;
  url?: string;
  description?: string;
  image?: string[];
  breadcrumb?: JsonLDBreadcrumbReference | JsonLDBreadcrumb[];
  breadcrumbs?: JsonLDBreadcrumb[];
  founder?: JsonLDFounder[];
  telephone?: string;
  email?: string;
  areaServed?: string;
  brand?: string;
  sameAs?: string[];
  publisher?: JsonLDPublisher;
  mainEntityData?: JsonLDMainEntity;
  isPartOf?: JsonLDIsPartOf;
  address?: JsonLDPostalAddress;
  geo?: JsonLDGeo;
  offers?: JsonLDOffer | JsonLDOffer[];
  mainEntityOfPage?:
    | {
        "@type": "WebPage";
        "@id": string;
      }
    | string;
  headline?: string;
  datePublished?: string;
  dateModified?: string;
  author?: {
    "@type": "Person";
    name: string;
    url?: string;
  }[];
  applicationCategory?: string;
  operatingSystem?: string;
  provider?: {
    "@type": "Organization";
    name: string;
    url: string;
  };
  inDefinedTermSet?: {
    "@type": "DefinedTermSet";
    name: string;
    url: string;
  };
};

function parseFAQPageMainEntity(mainEntityData: JsonLDFAQPageEntity[]) {
  return mainEntityData.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  }));
}

function parseServiceMainEntity(mainEntityData: JsonLDServiceMainEntity) {
  return {
    "@type": "Service",
    name: mainEntityData.name,
    description: mainEntityData.description,
    serviceType: mainEntityData.serviceType,
    provider: mainEntityData.provider
      ? {
          "@type": "Organization",
          name: mainEntityData.provider.name,
          url: mainEntityData.provider.url,
        }
      : undefined,
    potentialAction: mainEntityData.potentialAction
      ? {
          "@type": "SearchAction",
          name: mainEntityData.potentialAction.name,
          target: mainEntityData.potentialAction.target,
        }
      : undefined,
    category: mainEntityData.category,
    areaServed: mainEntityData.areaServed
      ? mainEntityData.areaServed.map((area) => ({
          "@type": area["@type"],
          name: area.name,
        }))
      : undefined,
  };
}

function parseCollectionPageMainEntity(
  mainEntityData: JsonLDCollectionPageEntity,
) {
  return {
    "@type": "ItemList",
    numberOfItems: mainEntityData.itemListUrls.length,
    itemListElement: mainEntityData.itemListUrls.map((url, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      url: url.startsWith("http") ? url : buildUrl(url), // Ensure URL is absolute
    })),
  };
}

function parsePersonMainEntity(mainEntityData: JsonLDPersonMainEntity) {
  return {
    "@type": "Person",
    name: mainEntityData.name,
    jobTitle: mainEntityData.jobTitle,
    image: mainEntityData.image,
    url: mainEntityData.url,
    description: mainEntityData.description,
    knowsAbout: mainEntityData.knowsAbout,
    sameAs: mainEntityData.sameAs,
    worksFor: mainEntityData.worksFor
      ? {
          "@type": "Organization",
          name: mainEntityData.worksFor.name,
        }
      : undefined,
  };
}

function parseMainEntity(mainEntityData: JsonLDMainEntity) {
  if (mainEntityData["@type"] === "FAQPage") {
    return parseFAQPageMainEntity(mainEntityData.mainEntity);
  }

  if (mainEntityData["@type"] === "Service") {
    return parseServiceMainEntity(mainEntityData.mainEntity);
  }

  if (mainEntityData["@type"] === "CollectionPage") {
    return parseCollectionPageMainEntity(mainEntityData.mainEntity);
  }

  if (mainEntityData["@type"] === "Person") {
    return parsePersonMainEntity(mainEntityData.mainEntity);
  }

  return [];
}

export function JsonLD({
  id,
  type,
  name,
  path,
  url,
  image,
  description,
  breadcrumb,
  breadcrumbs,
  telephone,
  email,
  areaServed,
  brand,
  founder,
  sameAs,
  publisher,
  mainEntityData,
  isPartOf,
  address,
  geo,
  offers,
  mainEntityOfPage,
  headline,
  datePublished,
  dateModified,
  author,
  applicationCategory,
  operatingSystem,
  provider,
  inDefinedTermSet,
}: JsonLDProps) {
  const isLocalBusiness = type === "LocalBusiness";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": type,
    "@id": id ? buildUrl(id) : undefined,
    name,
    logo: isLocalBusiness ? undefined : buildUrl("images/logo.png"),
    url: url ?? (path ? buildUrl(path) : undefined),
    image,
    description,
    breadcrumb: breadcrumb
      ? Array.isArray(breadcrumb)
        ? {
            "@type": "BreadcrumbList",
            itemListElement: breadcrumb.map((item, idx) => ({
              "@type": "ListItem",
              position: idx + 1,
              name: item.name,
              item: buildUrl(item.item),
            })),
          }
        : {
            "@id": breadcrumb._id,
          }
      : undefined,
    itemListElement:
      breadcrumbs && breadcrumbs.length > 0
        ? breadcrumbs.map((breadcrumb, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            name: breadcrumb.name,
            item: buildUrl(breadcrumb.item),
          }))
        : undefined,
    founder:
      founder && founder.length > 0
        ? founder.map((item) => ({
            "@type": "Person",
            name: item.name,
          }))
        : undefined,
    telephone,
    email: email ? `mailto:${email}` : undefined,
    areaServed: areaServed
      ? {
          "@type": "Place",
          name: areaServed,
        }
      : undefined,
    brand: brand
      ? {
          "@type": "Brand",
          name: brand,
        }
      : undefined,
    sameAs,
    publisher: publisher
      ? {
          "@id": publisher._id,
        }
      : undefined,
    mainEntity: mainEntityData ? parseMainEntity(mainEntityData) : undefined,
    isPartOf: isPartOf
      ? {
          "@type": "WebSite",
          name: isPartOf.name,
          url: isPartOf.url,
        }
      : undefined,
    address,
    geo,
    offers,
    mainEntityOfPage:
      typeof mainEntityOfPage === "string"
        ? {
            "@type": "WebPage",
            "@id": buildUrl(mainEntityOfPage),
          }
        : mainEntityOfPage,
    headline,
    datePublished,
    dateModified,
    author,
    applicationCategory,
    operatingSystem,
    provider,
    inDefinedTermSet,
  };

  return (
    <script
      type="application/ld+json"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD input should be sanitized
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

JsonLD.displayName = "JsonLD";
